import React, { useState } from 'react';
import { Button, message } from 'antd';
import type { TCredentialingContractBanner } from 'types/credentialing';
import type { SettingEnum } from 'types/enumerationData';
import { markCredentialingSignatureSigned } from 'api/credentialing';
import { getStateDisplayName } from 'utils/common';
import { SvgAlert } from '../SvgIcons';
import SignContractModal from '../SignContractModal';
import s from './s.module.less';

type Props = {
    contracts: TCredentialingContractBanner[];
    stateList?: SettingEnum[];
    isGroup?: boolean;
    onSigned?: () => void;
};

// Original Medicare has no payer contract: what the provider signs is their own PECOS application,
// by the e-signature email CMS sends them. Klarity marks the panel SIGNATURE_NEEDED until they do.
// Same test as the credentialing service's isOriginalMedicareName (engines/medicare-pecos/lib/macs.mjs):
// Medicare ADVANTAGE / Part C / Part D / dual / SNP / supplement plans are commercial contracts and
// keep the contract wording.
const isMedicare = (payerName: string) => {
    const raw = String(payerName || '').trim().toLowerCase();
    if (!raw) return false;
    if (/advantage|part\s*[cd]\b|ma-?pd|dual\s*complete|snp\b|supplement|medigap/.test(raw)) return false;
    return /^original medicare$|^medicare$|\bmedicare part b\b|\bfee.?for.?service medicare\b|\btraditional medicare\b/.test(raw);
};

const MEDICARE_SIGN_TIP =
    'CMS has emailed you a PIN and a link to e-sign your Medicare application in PECOS (from customerservice-donotreply@cms.hhs.gov). Medicare starts processing it once you sign.';

const NO_SIGN_URL_TIPS: { match: string; tip: string }[] = [
    {
        match: 'cigna',
        tip: 'A signing link has been sent to your email. Please check your inbox and complete the signature within 7 days.',
    },
];

const NO_SIGN_URL_DEFAULT_TIP = 'Your contract is being prepared. Our team will reach out to you with signing instructions shortly — please hold tight.';

const getNoSignUrlTip = (payerName: string) => {
    if (isMedicare(payerName)) return MEDICARE_SIGN_TIP;
    const name = payerName.toLowerCase();
    return NO_SIGN_URL_TIPS.find((t) => name.includes(t.match))?.tip || NO_SIGN_URL_DEFAULT_TIP;
};

const SignContractsBanner = ({ contracts, stateList, isGroup, onSigned }: Props) => {
    const [signingTarget, setSigningTarget] = useState<TCredentialingContractBanner | null>(null);
    const [confirmLoading, setConfirmLoading] = useState(false);

    if (contracts.length === 0) return null;

    const handleConfirm = async () => {
        if (!signingTarget) return;
        setConfirmLoading(true);
        try {
            const res = await markCredentialingSignatureSigned(
                signingTarget.contractId
                    ? { contractId: signingTarget.contractId }
                    : { panelId: signingTarget.panelId },
            );
            if (res && !res.error) {
                if (res.data?.data === false) {
                    message.warning('We could not confirm this contract. Please refresh and try again.');
                } else {
                    message.success('Contract signed status updated');
                }
                setSigningTarget(null);
                onSigned?.();
            }
        } finally {
            setConfirmLoading(false);
        }
    };

    const single = contracts.length === 1;
    const c0 = contracts[0];

    return (
        <div className={s.wrap}>
            {single ? (
                <div className={s.singleBanner}>
                    <div className={s.singleIcon}><SvgAlert /></div>
                    <div className={s.singleBody}>
                        <div className={s.singleName}>
                            Your {c0.payerName} {isMedicare(c0.payerName) ? 'application' : 'contract'} for {getStateDisplayName(c0.payerState, stateList)} is ready to sign
                        </div>
                        {/* Medicare's instruction sits UNDER the title, where the countersign line was: the
                            right-hand slot has no width limit, and a long tip there squeezed the title to one
                            word per line. */}
                        <div className={s.singleDetail}>
                            {isMedicare(c0.payerName)
                                ? MEDICARE_SIGN_TIP
                                : `Sign it and ${c0.payerName} countersigns; then your ${getStateDisplayName(c0.payerState, stateList)} panel goes live. It takes a few minutes.`}
                        </div>
                    </div>
                    {isMedicare(c0.payerName) ? null : c0.signUrl ? (
                        <Button className={s.signBtn} onClick={() => setSigningTarget(c0)}>
                            Review and sign
                        </Button>
                    ) : (
                        <div className={s.singleDetail}>{getNoSignUrlTip(c0.payerName)}</div>
                    )}
                </div>
            ) : (
                <div className={s.multiCard}>
                    <div className={s.multiHeader}>
                        <div className={s.singleIcon}><SvgAlert /></div>
                        <div className={s.multiHeaderBody}>
                            <div className={s.multiTitle}>
                                {contracts.length} contracts are ready to sign
                            </div>
                            <div className={s.multiSubtitle}>
                                Sign each one and the payer countersigns; then that panel goes live. A few minutes each.
                            </div>
                        </div>
                    </div>
                    {contracts.map((c, i) => (
                        <div key={c.contractId || c.panelId} className={`${s.multiRow}${i > 0 ? ` ${s.withBorder}` : ''}`}>
                            <div className={s.rowLabel}>
                                {c.payerName} <span className={s.rowState}>&middot; {getStateDisplayName(c.payerState, stateList)}{isGroup && c.providerName ? ` · ${c.providerName}` : ''}</span>
                            </div>
                            {c.signUrl ? (
                                <Button className={s.signBtn} onClick={() => setSigningTarget(c)}>
                                    Review and sign
                                </Button>
                            ) : (
                                <div className={s.singleDetail}>{getNoSignUrlTip(c.payerName)}</div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <SignContractModal
                open={!!signingTarget}
                payerName={signingTarget?.payerName || ''}
                state={getStateDisplayName(signingTarget?.payerState, stateList) || ''}
                signingUrl={signingTarget?.signUrl}
                confirmLoading={confirmLoading}
                onClose={() => setSigningTarget(null)}
                onConfirm={handleConfirm}
            />
        </div>
    );
};

export default SignContractsBanner;
